<template>
  <ControlWrapper v-bind="controlWrapper" :styles="styles" :applied-options="appliedOptions" :error="control.errors">
    <SectionWrapper class="!mb-0">
      <template #header>
        <div class="flex w-full items-center justify-between">
          <h3
            v-if="previewImages?.length || checkTheme('default', 'tesla', 'aa', 'suzuki', 'common')"
            :class="styles.control.label"
          >
            {{ imageSectionData.title }}
          </h3>
          <div class="flex flex-row gap-2">
            <WButton v-if="previewImages?.[0]?.key" type="ghost" :disabled="!control.enabled" @click="openImageUpload">
              <WidgetIcon
                :icon="imageSectionData.editIcon || themeIcons.edit"
                height="1.5rem"
                class="widget-action-icon"
              />
            </WButton>
            <WButton
              v-if="previewImages?.[0]?.key"
              type="ghost"
              :disabled="!control.enabled"
              @click="isDeleteModalOpen = true"
            >
              <WidgetIcon
                :icon="imageSectionData.deleteIcon || themeIcons.trash"
                class="widget-action-icon-negative"
                height="1.5rem"
              />
            </WButton>
          </div>
        </div>
      </template>
      <ActionCard
        v-if="!previewImages?.[0]?.key"
        :title="imageSectionData.cardTitle"
        :icon="usedIcon"
        :content="imageSectionData.cardContent"
        :disabled="!control.enabled"
        action="Add photos"
        @action="openImageUpload"
      ></ActionCard>
      <div
        v-else
        class="grid w-full grid-cols-[repeat(auto-fill,_minmax(80px,_1fr))] gap-4 sm:grid-cols-[repeat(auto-fill,_minmax(120px,_1fr))]"
      >
        <div class="relative h-full w-full" v-for="(image, index) in previewImages" :key="image.key">
          <img
            :src="image.previewUrl"
            height="80"
            :alt="`vehicle-damage${index}`"
            class="aspect-square w-full rounded-custom object-cover sm:h-28"
          />
          <WButton
            type="ghost"
            class="absolute right-1 top-1 rounded-full bg-backgroundSoft"
            :disabled="!control.enabled"
            @click="deleteImage(index)"
          >
            <WidgetIcon :icon="themeIcons.delete" height="1rem" class="widget-action-icon-negative" />
          </WButton>
        </div>
      </div>
      <ImageUpload
        v-model="isImageUploadOpen"
        :title="imageSectionData.cardTitle"
        :info="imageSectionData.cardContent"
        :key="String(isImageUploadOpen)"
        :images="previewImages"
        @imagesAdded="saveimages"
      ></ImageUpload>
    </SectionWrapper>
    <Modal v-if="isDeleteModalOpen" v-model="isDeleteModalOpen" hide-close>
      <template #header><p class="title2-bold">Delete images?</p> </template>
      <div class="flex h-full w-full flex-col items-start gap-4">
        <p>Are you sure you want to delete all of your uploaded images?</p>
        <p>This action can not be undone</p>
      </div>
      <template #footer>
        <div class="w-full">
          <WButton type="secondary" class="widget-confirm-modal_btn_close" @click="isDeleteModalOpen = false">
            No, keep images
          </WButton>
          <WButton type="primary" class="widget-confirm-modal_btn" @click="clearImages"> Yes, I am sure </WButton>
        </div>
      </template>
    </Modal>
  </ControlWrapper>
</template>

<script lang="ts">
  import Modal from 'src/components/base/ConfirmModal.vue';
  import { ref, defineComponent, computed } from 'vue';
  import { ControlElement } from '@jsonforms/core';

  import { rendererProps, useJsonFormsControl, RendererProps } from '@jsonforms/vue';
  import { useWidgetControl } from '../util';

  import Button from '../../base/Button.vue';
  import ActionCard from '../../base/ActionCard.vue';
  import ImageUpload from '../../base/uploader/ImageUpload.vue';
  import SectionWrapper from '../../claims-widget/components/SectionWrapper.vue';
  import ControlWrapper from './ControlWrapper.vue';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import { ImageSection } from 'src/types/widget';
  import { config, themeIcons, checkTheme } from 'src/utils/config';

  export type ImageSectionData = {
    title: string;
    cardTitle: string;
    cardContent: string;
    icon: string;
    editIcon: string;
    deleteIcon: string;
  };

  export default defineComponent({
    components: {
      WButton: Button,
      ActionCard,
      ImageUpload,
      SectionWrapper,
      ControlWrapper,
      WidgetIcon,
      Modal,
    },
    props: {
      ...rendererProps<ControlElement>(),
    },
    setup(props: RendererProps<ControlElement>) {
      const isImageUploadOpen = ref(false);
      const isDeleteModalOpen = ref(false);
      const control = useWidgetControl(useJsonFormsControl(props));
      const previewImages = ref<ImageSection[]>(control.control.value.data);

      const imageSectionData = ref(
        (control.control.value.schema as unknown as { imageSection: ImageSectionData }).imageSection
      );

      const usedIcon = computed(() => {
        if (imageSectionData.value.icon) {
          return imageSectionData.value.icon;
        }
        if (checkTheme('cinch', 'crashdaddy', 'tesla', 'aa', 'suzuki', 'common')) {
          return themeIcons.value.image;
        }
        return '';
      });

      function openImageUpload() {
        isImageUploadOpen.value = true;
      }
      function saveimages(images: ImageSection[]) {
        isImageUploadOpen.value = false;
        previewImages.value = images;
        control.onChange(images);
      }

      function clearImages() {
        previewImages.value = [];
        control.onChange([]);
        isDeleteModalOpen.value = false;
      }

      function deleteImage(index: number) {
        previewImages.value.splice(index, 1);
        control.onChange(previewImages.value);
      }

      return {
        isImageUploadOpen,
        previewImages,
        imageSectionData,
        isDeleteModalOpen,
        themeIcons,
        usedIcon,
        openImageUpload,
        saveimages,
        clearImages,
        deleteImage,
        checkTheme,
        ...control,
        config,
      };
    },
  });
</script>
